import { useCallback, useEffect, useState } from 'react';
import {
  ADMIN_ACCOUNTS_PAGE_SIZE,
  getAdminAccountsReadModel,
  type AdminAccountCursor,
  type AdminAccountDirectoryItem,
  type AdminAccountsReadModel,
} from '../services/adminAccountsService';

export interface UseAdminAccountsState {
  data: AdminAccountsReadModel | null;
  accounts: AdminAccountDirectoryItem[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  return 'Nie udało się odczytać monitora kont.';
};

export const useAdminAccounts = (): UseAdminAccountsState => {
  const [data, setData] = useState<AdminAccountsReadModel | null>(null);
  const [accounts, setAccounts] = useState<AdminAccountDirectoryItem[]>([]);
  const [cursor, setCursor] = useState<AdminAccountCursor>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const readModel = await getAdminAccountsReadModel(ADMIN_ACCOUNTS_PAGE_SIZE);
      setData(readModel);
      setAccounts(readModel.accounts);
      setCursor(readModel.cursor);
    } catch (readError) {
      console.error('[useAdminAccounts] Accounts read failed:', readError);
      setError(getErrorMessage(readError));
      setData(null);
      setAccounts([]);
      setCursor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!data?.hasMore || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const readModel = await getAdminAccountsReadModel(ADMIN_ACCOUNTS_PAGE_SIZE, cursor);
      setData((current) =>
        current
          ? {
              ...readModel,
              metrics: readModel.metrics,
              accounts: [...accounts, ...readModel.accounts],
              orphanProviderPreviews: readModel.orphanProviderPreviews,
            }
          : readModel
      );
      setAccounts((current) => [...current, ...readModel.accounts]);
      setCursor(readModel.cursor);
    } catch (readError) {
      console.error('[useAdminAccounts] Accounts load more failed:', readError);
      setError(getErrorMessage(readError));
    } finally {
      setLoadingMore(false);
    }
  }, [accounts, cursor, data?.hasMore, loadingMore]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    data,
    accounts,
    loading,
    loadingMore,
    error,
    refresh,
    loadMore,
  };
};
